<template>
  <transition name="fade">
    <div
      v-if="MULTIBILL_EDIT.show"
      v-preventWindowScrolling
      class="modal-conteiner"
      @click.self="close"
    >
      <div class="modal-frame" :class="{ 'shown': isShown }">
        <div class="modal-inner-container">
          <div>
            <div>
              <form @submit.prevent="handleChangeAmount">
                <fieldset>
                  <div class="container">
                    <div v-m-b="20">
                      <span>{{ $t('change.bill.amount') }}</span>
                    </div>
                    <labeled-input
                      v-model="amount"
                      :label="$t('enter.new.amount', { getCurrencySign: currency.sign })"
                      :placeholder="`0 ${currency.sign}`"
                      :error="$v.amount.$error"
                      :force-replace="[
                        [/[^0-9\.\,]+/g, ''],
                        [/^\./g, ''],
                        [/\,/, '\.'],
                      ]"
                      :force-format="[$formatInputAmount({
                        significantZeros: currency.significant_zeros,
                        decimalsSeparator: '.',
                        thousandSeparator: ' ',
                      })]"
                      @focus="$v.amount.$reset"
                      @blur="$v.amount.$touch"
                    />
                  </div>
                </fieldset>
                <div class="buttons_wrapper">
                  <button class="button" :disabled="!isValid" @click.prevent="handleChangeAmount">
                    {{ $t('common.apply') }}
                  </button>
                  <button class="button blank" @click.prevent="close">
                    <span>{{ $t('common.cancel') }}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import HTTPService from '@/services/Http';

import { isLessMaxAmount, isMoreMinAmount } from '@/components/inputElements/CustomValidators';

import LabeledInput from 'molecules/common/LabeledInput';

export default {
  name: 'ModalMultiBillEdit',
  components: {
    LabeledInput,
  },
  data() {
    return {
      sending: false,
      isShown: false,
      amount: '',
      limits: {},
    };
  },
  validations() {
    return {
      amount: {
        minValue: isMoreMinAmount(this.minLimitAmount, this.currency),
        maxValue: isLessMaxAmount(this.maxLimitAmount, 0, this.currency),
      },
    };
  },
  computed: {
    currency() {
      return this.PROJECT_INFO.currency;
    },
    isValid() {
      if (this.$validate.isEmptyString(this.amount)) {
        return true;
      }

      return !this.$v.amount.$invalid;
    },
    paymentEnabled() {
      const { bill } = this.MULTIBILL_EDIT;

      if (this.$validate.isEmptyObject(bill)) {
        return null;
      }

      return bill.billPaymentEnabled;
    },
    billTariff() {
      const { bill, tariffId } = this.MULTIBILL_EDIT;

      if (this.$validate.isEmptyObject(bill)) {
        return null;
      }

      const billTariff = bill.billTariffs.find(tariff => tariff.id === tariffId);

      if (this.$validate.isUndefined(billTariff)) {
        return null;
      }

      return billTariff;
    },
    isSubscription() {
      if (this.$validate.isNull(this.billTariff)) {
        return false;
      }

      return this.$validate.isNotNull(this.billTariff.subscription);
    },
    minLimitAmount() {
      if (this.isSubscription) {
        const { oneTimePayment } = this.billTariff.subscription;

        return oneTimePayment
          ? Math.max(Number(this.limits.min_amount), Number(this.limits.min_recurrent_amount))
          : Number(this.limits.min_recurrent_amount);
      }

      return this.limits.min_amount;
    },
    maxLimitAmount() {
      if (this.isSubscription) {
        const { oneTimePayment } = this.billTariff.subscription;

        return oneTimePayment
          ? Math.min(Number(this.limits.max_amount), Number(this.limits.max_recurrent_amount))
          : Number(this.limits.max_recurrent_amount);
      }

      return Number(this.limits.max_amount);
    },
    formattedAmount() {
      const replacedAmount = this.amount.replace(/\s/g, '');
      const preparedAmount = Math.round(replacedAmount * 10 ** this.currency.significant_zeros);

      return this.$validate.isPositiveNumber(preparedAmount) ? preparedAmount : null;
    },
    ...mapState({
      PROJECT_INFO: state => state.PROJECT_INFO,
      MULTIBILL_EDIT: state => state.MODALS.MULTIBILL_EDIT,
    }),
  },
  watch: {
    MULTIBILL_EDIT: {
      deep: true,
      async handler(value) {
        if (!value.show) {
          return this.isShown = false;
        }

        if (value.limits) {
          this.limits = value.limits;
        } else {
          await this.loadLimits();
        }

        setTimeout(() => this.isShown = true, 250);
      },
    },
  },
  methods: {
    async loadLimits() {
      try {
        const { data } = await HTTPService.getBillLimits();

        this.limits = data;
      } catch (error) {
        this.$alert({
          type: 'warning',
          text: 'data.not.loaded',
        });

        this.close();
      }
    },
    getPayload() {
      const editableTariffId = this.MULTIBILL_EDIT.tariffId;
      const projectCurrency = this.PROJECT_INFO.currency;

      const updatedBillTariffs = this.MULTIBILL_EDIT.bill.billTariffs.map(tariff => {
        const isCurrent = editableTariffId === tariff.id;

        return {
          ...tariff,
          amount: isCurrent ? this.formattedAmount : tariff.amount,
          currency: isCurrent ? projectCurrency : tariff.currency,
        };
      });

      return {
        billTariffs: updatedBillTariffs,
        tips: this.MULTIBILL_EDIT.tips || null,
      };
    },
    async handleChangeAmount() {
      if (!this.isValid || this.sending) {
        return;
      }

      this.sending = true;

      const payload = this.getPayload();

      try {
        const { data } = await HTTPService.editBill(
          payload,
          this.MULTIBILL_EDIT.bill.public_id
        );

        this.$alert({
          type: 'success',
          text: 'bill.amount.changed',
        });

        this.CHANGE_SELECTED_TRANSACTION_CURRENCY(data.currency);
        this.CHANGE_SELECTED_TRANSACTION_BILL_TARIFFS(data.billTariffs);
        this.CHANGE_SELECTED_TRANSACTION_APPLY_PROMO_CODE(data.applyPromoCode);
        this.CHANGE_SELECTED_TRANSACTION_PROMO_CODE_APPLICABLE(data.promoCodeApplicable);
      } catch {
        this.$alert({
          type: 'warning',
          text: 'problems.on.server',
        });
      }

      this.sending = false;
      this.close();
    },
    close() {
      this.amount = '';
      this.limits = {};
      this.isShown = false;

      this.SET_MODAL_PARAMS({
        type: 'MULTIBILL_EDIT',
        params: {
          show: false,
          bill: {},
          tariffId: null,
        },
      });
    },
    ...mapMutations([
      'SET_MODAL_PARAMS',
      'CHANGE_SELECTED_TRANSACTION_CURRENCY',
      'CHANGE_SELECTED_TRANSACTION_BILL_TARIFFS',
      'CHANGE_SELECTED_TRANSACTION_APPLY_PROMO_CODE',
      'CHANGE_SELECTED_TRANSACTION_PROMO_CODE_APPLICABLE',
    ]),
  },
};
</script>

<style lang="sass" scoped>
.modal-conteiner
    /* This way it could be display flex or grid or whatever also. */
    display: block

    /* Probably need media queries here */
    // width: 600px
    width: 100%
    max-width: 100%

    // height: 400px
    height: 100%
    max-height: 100%
    position: fixed
    z-index: 2000
    left: 50%
    top: 50%
    transform: translate(-50%, -50%)
    background-color: transparentize(#B979E5, .75)
    // opacity: 1

    // background-color: #1E1D2F
    // background-color: rgba(30, 29, 47, .4)
.modal-frame
    position: absolute
    // width: auto
    width: 100%
    max-width: 36rem
    left: 50%
    top: calc(100% + 16rem)
    transform: translate(-50%, -50%)
    overflow: auto
    transition: top .2s ease
    &.shown
        top: 50%
        overflow: hidden
.modal-inner-container
    max-width: 36rem
    margin: auto
    // align-content: initial
    // min-height: 100vh
    // width: 100%
    > div
        flex-basis: 100%
        position: relative
        margin: calc(.64rem * 2) 0
        padding: .64rem
        background-color: #181824
        border: 0.12rem solid #181824
        border-radius: .8rem
        @media (min-width: 32rem)
            padding: 0 2.4rem 1.2rem
            margin: 2.4rem 0
        > div
            margin: 0
            @media (min-width: 32rem)
                margin: 1.2rem 0
// .modal-conteiner
//     /* This way it could be display flex or grid or whatever also. */
//     display: block

//     /* Probably need media queries here */
//     // width: 600px
//     width: 100%
//     max-width: 100%

//     // height: 400px
//     height: 100%
//     max-height: 100%
//     position: fixed
//     z-index: 101
//     left: 50%
//     top: 50%
//     transform: translate(-50%, -50%)
//     // background-color: #1E1D2F
//     background-color: rgba(30, 29, 47, .4)
// .modal-frame
//     position: absolute
//     top: 0
//     left: 0
//     width: auto
//     width: 100%
//     left: 50%
//     top: 50%
//     transform: translate(-50%, -50%)
//     height: 100%
//     overflow: auto
//     padding: 0 .64rem
//     padding-top: 35vh
//     @media (min-width: 32rem)
//         padding: 0 1.28rem
//         padding-top: 35vh
.modal-inner-container
  // max-width: 36rem
  // margin: auto
  // position: relative
  // overflow: hidden
  // // align-content: initial
  // // min-height: 100vh
  // // width: 100%
  &::before
    background:
      image: get-icon("settings", #fff)
      repeat: no-repeat
      position: center center
      size: contain
    content: ''
    opacity: .4
    width: 8.9rem
    height: 8.9rem
    position: absolute
    right: -3.45rem
    top: 2.5rem
    z-index: 10
  > div
    // margin: 0 auto !important
    // flex-basis: 100%
    // position: relative
    // margin: calc(.64rem * 2) 0
    // // padding: .64rem
    padding: 3.4rem 3.57rem 3.57rem !important
    @media (max-width: 480px)
      padding: 1.4rem 1.57rem 1.57rem !important
    @media (min-width: 481px) and (max-width: 767px)
      padding: 2.4rem 2.57rem 2.57rem !important
    form
      position: relative
      z-index: 11
      div.input
        margin-top: 30px !important
    // .eclipse &
    //   background-color: #E5E5E5
    //   border: 0.12rem solid #E5E5E5
    // .obsidian &
    //   background-color: #181824
    //   border: 0.12rem solid #181824
    // border-radius: .8rem
    @media (min-width: 32rem)
        margin: 0
    > div
        margin: 0 !important

.container
    > div
        flex-basis: 100%
        span
          font-size: 1.4rem
          font-weight: 600
        &:first-of-type
            font-size: 16px
            line-height: 22px
            // margin-bottom: 20px
            @media (min-width: 32rem)
                font-size: 18px

.buttons_wrapper
  margin-top: 25px
  display: flex
  justify-content: space-between
  align-items: center
  button
    @media (min-width: 480px)
      width: 65%
      max-width: 67.5rem
      flex-grow: 1
    @media (max-width: 479px)
      width: 58%
      font-size: 16px
  .button.blank
    text-align: center
    font-size: 1.2rem
    font-weight: 500
    padding-left: 1.85rem
    padding-right: 0
    width: auto
    text-align: right
</style>
